/**
 * Supabase client for the admin panel.
 *
 * Uses the ANON key, exactly like the storefront. That is not an oversight — the
 * admin panel gets its privileges from the signed-in user's JWT, which row level
 * security evaluates through is_admin(). The service-role key would bypass RLS
 * entirely and would be readable by anyone who opened the network tab, handing
 * them the whole order book. It must never appear in this app.
 */

import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

export const isSupabaseConfigured = Boolean(url && anonKey)

/**
 * Demo mode: simulated data, no backend.
 *
 * On by default when no credentials are present, so a fresh clone runs and can
 * be looked at. Can also be forced with VITE_DEMO=1 to demonstrate the panel
 * without touching live figures.
 */
export const isDemo = import.meta.env.VITE_DEMO === '1' || !isSupabaseConfigured

export const supabase = isSupabaseConfigured
  ? createClient(url, anonKey, {
      auth: {
        // The admin panel *does* want a durable session — a shop assistant
        // should not be signed out between tasks.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'coquis-admin-auth',
      },
      global: { headers: { 'x-application-name': 'coquis-admin' } },
    })
  : null

if (isDemo && import.meta.env.DEV) {
  console.info(
    '[coquis-admin] Running in DEMO mode with simulated data. ' +
      'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env to connect to the database.',
  )
}
